import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Plus, Loader2, Search, FolderOpen, Camera, StickyNote, Ruler, CalendarDays } from "lucide-react";
import { toast } from "sonner";
import { StudioLayout } from "./StudioLayout";
import { OfflineBanner } from "@/components/OfflineBanner";
import { StatusBadge, EmptyState, ListSkeleton } from "@/components/studio/StudioUI";
import { isOffline, queueDossier, localDrafts, removeDraft, queueChild } from "@/lib/offline";
import { DateField } from "@/components/DateField";
import { studio, apiError, beDate, inputCls, btnPrimary, btnGhost, TRADE_OPTIONS, JOB_TYPES } from "@/lib/studioApi";

const GROUPS = {
  a_traiter: ["nouveau", "visite_a_effectuer", "en_preparation", "devis_a_creer"],
  devis: ["devis_envoye", "en_attente", "a_relancer"],
  chantier: ["accepte", "chantier_en_cours"],
  clos: ["chantier_termine", "refuse", "archive"],
};
const FILTERS = [["tous", "Tous"], ["a_traiter", "À traiter"], ["devis", "Devis en cours"], ["chantier", "Chantiers"], ["clos", "Clôturés"]];
const groupOf = (status) => Object.keys(GROUPS).find((g) => GROUPS[g].includes(status)) || "a_traiter";
const normalizeFilter = (v) => (!v ? "tous" : GROUPS[v] ? v : groupOf(v));

const emptyForm = {
  client_id: "", client_name: "", client_phone: "", client_email: "",
  site_address: "", site_postal_code: "", site_city: "",
  trade: "chauffagiste", job_type: "remplacement_chaudiere", visit_date: "", summary: "",
};

export default function StudioDossiers() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [dossiers, setDossiers] = useState(null);
  const [clients, setClients] = useState([]);
  const [filter, setFilter] = useState(normalizeFilter(params.get("filtre")));
  const [search, setSearch] = useState("");
  const [showForm, setShowForm] = useState(params.get("nouveau") === "1");
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [drafts, setDrafts] = useState(localDrafts());
  const [draftEntry, setDraftEntry] = useState({});

  const load = useCallback(() => {
    studio.get("/dossiers").then(({ data }) => setDossiers(data)).catch(() => setDossiers([]));
    studio.get("/clients").then(({ data }) => setClients(data)).catch(() => {});
  }, []);

  useEffect(() => { load(); }, [load]);

  const saveOffline = () => {
    queueDossier(form);
    setDrafts(localDrafts());
    setForm(emptyForm);
    setShowForm(false);
    setParams({});
    toast.success("Dossier enregistré sur l'appareil — il sera envoyé au retour du réseau.");
  };

  const addToDraft = (localId, field) => {
    const value = (draftEntry[`${localId}-${field}`] || "").trim();
    if (!value) return;
    queueChild(localId, field, field === "materials" ? { label: value, quantity: "1" } : { text: value });
    setDraftEntry((d) => ({ ...d, [`${localId}-${field}`]: "" }));
    setDrafts(localDrafts());
    toast.success(field === "materials" ? "Matériel noté hors ligne." : "Note enregistrée hors ligne.");
  };

  const create = async () => {
    if (isOffline()) return saveOffline();
    setSaving(true);
    try {
      const { data } = await studio.post("/dossiers", form);
      toast.success(`Dossier ${data.reference} créé.`);
      setForm(emptyForm);
      setShowForm(false);
      setParams({});
      navigate(`/studio/dossiers/${data.id}`);
    } catch (e) {
      if (!e.response) {
        saveOffline();
      } else {
        toast.error(apiError(e, "Création impossible."));
      }
    } finally {
      setSaving(false);
    }
  };

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    return (dossiers || []).filter((d) => {
      if (filter !== "tous" && groupOf(d.status) !== filter) return false;
      if (!q) return true;
      return [d.reference, d.client_name, d.site_city, d.title].some((v) => (v || "").toLowerCase().includes(q));
    });
  }, [dossiers, filter, search]);
  const counts = useMemo(() => (dossiers || []).reduce((acc, d) => {
    ["tous", groupOf(d.status)].forEach((k) => { acc[k] = (acc[k] || 0) + 1; });
    return acc;
  }, {}), [dossiers]);
  const jobTypes = JOB_TYPES[form.trade] || JOB_TYPES.autre;

  return (
    <StudioLayout
      title="Dossiers"
      subtitle="Un dossier par chantier : capturez sur place, complétez pendant la journée."
      action={
        <button type="button" data-testid="new-dossier-btn" onClick={() => setShowForm(!showForm)} className={btnPrimary}>
          <Plus className="w-4 h-4" /> Nouveau dossier
        </button>
      }
    >
      <OfflineBanner onSynced={() => { load(); setDrafts(localDrafts()); }} />

      {!!drafts.length && (
        <div data-testid="offline-drafts" className="mt-5 space-y-3">
          {drafts.map((d) => (
            <div key={d.localId} data-testid={`offline-draft-${d.localId}`} className="rounded-2xl border border-amber-200 bg-amber-50/70 p-5">
              <div className="flex flex-wrap items-center gap-2">
                <span className="rounded-full border border-amber-300 bg-white px-2.5 py-0.5 text-[11px] font-bold text-amber-800">Hors ligne</span>
                <span className="text-xs text-[#5C6A87]">{(d.children || []).length} élément(s) capturé(s)</span>
                <button type="button" data-testid={`draft-delete-${d.localId}`} onClick={() => { removeDraft(d.localId); setDrafts(localDrafts()); }} className="ml-auto text-xs font-semibold text-[#5C6A87] hover:text-red-600 transition-colors">
                  Supprimer
                </button>
              </div>
              <p className="mt-2 font-heading text-base font-semibold text-[#0F2645]">{d.payload.client_name}</p>
              <p className="text-sm text-[#5C6A87]">{d.payload.summary || d.payload.site_city || "Brouillon de chantier"}</p>
              <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-2">
                <div className="flex gap-2">
                  <input data-testid={`draft-note-input-${d.localId}`} className={inputCls} placeholder="Note rapide" value={draftEntry[`${d.localId}-notes`] || ""} onChange={(e) => setDraftEntry({ ...draftEntry, [`${d.localId}-notes`]: e.target.value })} />
                  <button type="button" data-testid={`draft-note-add-${d.localId}`} onClick={() => addToDraft(d.localId, "notes")} className={btnGhost}>Note</button>
                </div>
                <div className="flex gap-2">
                  <input data-testid={`draft-material-input-${d.localId}`} className={inputCls} placeholder="Matériel à acheter" value={draftEntry[`${d.localId}-materials`] || ""} onChange={(e) => setDraftEntry({ ...draftEntry, [`${d.localId}-materials`]: e.target.value })} />
                  <button type="button" data-testid={`draft-material-add-${d.localId}`} onClick={() => addToDraft(d.localId, "materials")} className={btnGhost}>Matériel</button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {showForm && (
        <div data-testid="dossier-form" className="mt-6 rounded-2xl border border-[#E2E7F7] bg-white p-5 shadow-[0_1px_2px_rgba(15,38,69,0.04)] space-y-3">
          <select
            data-testid="dossier-client-select"
            className={inputCls}
            value={form.client_id}
            onChange={(e) => {
              const client = clients.find((c) => c.id === e.target.value);
              setForm((f) => ({
                ...f,
                client_id: e.target.value,
                client_name: client?.name || "",
                client_phone: client?.phone || "",
                client_email: client?.email || "",
                site_address: client?.address || f.site_address,
                site_postal_code: client?.postal_code || f.site_postal_code,
                site_city: client?.city || f.site_city,
              }));
            }}
          >
            <option value="">Nouveau client (saisie rapide)</option>
            {clients.map((c) => <option key={c.id} value={c.id}>{`${c.name}${c.city ? ` — ${c.city}` : ""}`}</option>)}
          </select>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <input data-testid="dossier-client-name" className={inputCls} placeholder="Nom du client *" value={form.client_name} onChange={(e) => setForm((f) => ({ ...f, client_name: e.target.value }))} />
            <input data-testid="dossier-client-phone" className={inputCls} placeholder="Téléphone" value={form.client_phone} onChange={(e) => setForm((f) => ({ ...f, client_phone: e.target.value }))} />
            <input data-testid="dossier-client-email" className={inputCls} placeholder="Email" value={form.client_email} onChange={(e) => setForm((f) => ({ ...f, client_email: e.target.value }))} />
            <input data-testid="dossier-address" className={inputCls} placeholder="Adresse du chantier" value={form.site_address} onChange={(e) => setForm((f) => ({ ...f, site_address: e.target.value }))} />
            <input data-testid="dossier-postal" className={inputCls} placeholder="Code postal" value={form.site_postal_code} onChange={(e) => setForm((f) => ({ ...f, site_postal_code: e.target.value }))} />
            <input data-testid="dossier-city" className={inputCls} placeholder="Commune" value={form.site_city} onChange={(e) => setForm((f) => ({ ...f, site_city: e.target.value }))} />
            <select data-testid="dossier-trade" className={inputCls} value={form.trade} onChange={(e) => setForm((f) => ({ ...f, trade: e.target.value, job_type: (JOB_TYPES[e.target.value] || JOB_TYPES.autre)[0][0] }))}>
              {TRADE_OPTIONS.map(([v, l]) => <option key={v} value={v} >{l}</option>)}
            </select>
            <select data-testid="dossier-jobtype" className={inputCls} value={form.job_type} onChange={(e) => setForm((f) => ({ ...f, job_type: e.target.value }))}>
              {jobTypes.map(([v, l]) => <option key={v} value={v} >{l}</option>)}
            </select>
            <DateField testId="dossier-visit-date" placeholder="Date de visite" value={form.visit_date} onChange={(v) => setForm((f) => ({ ...f, visit_date: v }))} />
          </div>
          <textarea data-testid="dossier-summary" rows={2} className={inputCls} placeholder="En deux mots : que faut-il faire ?" value={form.summary} onChange={(e) => setForm((f) => ({ ...f, summary: e.target.value }))} />
          <div className="flex gap-2">
            <button type="button" data-testid="dossier-save-btn" disabled={saving || !form.client_name.trim()} onClick={create} className={btnPrimary}>
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : null} Créer le dossier
            </button>
            <button type="button" onClick={() => { setShowForm(false); setParams({}); }} className={btnGhost}>Annuler</button>
          </div>
        </div>
      )}

      <div data-testid="dossiers-tabs" className="-mx-4 mt-6 flex gap-1 overflow-x-auto border-b border-[#E2E7F7] px-4 sm:-mx-6 sm:px-6 lg:mx-0 lg:px-0">
        {FILTERS.map(([v, l]) => {
          const active = filter === v;
          return (
            <button
              key={v}
              type="button"
              data-testid={`dossiers-filter-${v}`}
              onClick={() => setFilter(v)}
              className={`-mb-px inline-flex shrink-0 items-center gap-1.5 border-b-2 px-3 pb-2.5 pt-1 text-sm font-semibold transition-colors duration-200 ${active ? "border-[#3A5CC8] text-[#3A5CC8]" : "border-transparent text-[#0F2645] hover:border-[#C7D2F5]"}`}
            >
              {l}
              <span className={`rounded-full px-1.5 py-0.5 text-[10px] font-bold tabular-nums ${active ? "bg-[#EEF1FC] text-[#3A5CC8]" : "bg-[#F3F5FC] text-[#5C6A87]"}`}>{counts[v] || 0}</span>
            </button>
          );
        })}
      </div>

      <div className="mt-4 relative">
        <Search className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#93A0BC]" />
        <input data-testid="dossiers-search" className={`${inputCls} pl-9 bg-[#F6F8FD]`} placeholder="Rechercher un dossier…" value={search} onChange={(e) => setSearch(e.target.value)} />
      </div>

      <p data-testid="dossiers-count" className="mt-3 text-sm text-[#5C6A87]">{visible.length} dossier{visible.length > 1 ? "s" : ""}</p>

      <div data-testid="dossiers-list" className="mt-3 overflow-hidden rounded-2xl border border-[#E2E7F7] bg-white shadow-[0_1px_2px_rgba(15,38,69,0.04)]">
        {dossiers === null && <ListSkeleton />}
        {visible.map((d, i) => (
          <button
            key={d.id}
            type="button"
            data-testid={`dossier-card-${d.id}`}
            onClick={() => navigate(`/studio/dossiers/${d.id}`)}
            style={{ animationDelay: `${Math.min(i, 8) * 30}ms` }}
            className="dv-card-in block w-full border-b border-[#EEF1FC] px-4 py-3.5 text-left transition-colors last:border-b-0 hover:bg-[#F6F8FD] sm:px-5"
          >
            <div className="flex items-center justify-between gap-3">
              <p className="min-w-0 truncate font-heading text-sm font-extrabold text-[#0F2645]">
                {d.reference}
                <span className="ml-2 text-xs font-semibold text-[#93A0BC]">{d.trade_label}</span>
              </p>
              <StatusBadge value={d.status} />
            </div>
            <div className="mt-1.5 flex items-center justify-between gap-3">
              <p className="flex min-w-0 items-center gap-2 text-sm text-[#0F2645]">
                <span className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-orange-100 text-orange-500"><FolderOpen className="h-3.5 w-3.5" strokeWidth={2.2} /></span>
                <span className="truncate">{d.client_name}<span className="text-[#93A0BC]">{d.site_city ? ` · ${d.site_city}` : ""}{d.title && d.title !== d.client_name ? ` · ${d.title}` : ""}</span></span>
              </p>
              <p className="hidden shrink-0 items-center gap-2.5 text-[11px] font-semibold text-[#93A0BC] sm:inline-flex">
                <span className="inline-flex items-center gap-1"><Camera className="h-3 w-3" />{d.photos?.length || 0}</span>
                <span className="inline-flex items-center gap-1"><StickyNote className="h-3 w-3" />{d.notes?.length || 0}</span>
                <span className="inline-flex items-center gap-1"><Ruler className="h-3 w-3" />{d.measures?.length || 0}</span>
                {d.visit_date && <span className="inline-flex items-center gap-1 text-[#3A5CC8]"><CalendarDays className="h-3 w-3" />{beDate(d.visit_date)}</span>}
              </p>
            </div>
          </button>
        ))}
        {dossiers !== null && !visible.length && (
          <EmptyState
            testId="dossiers-empty"
            icon={FolderOpen}
            title={dossiers.length ? "Aucun dossier ne correspond" : "Aucun dossier pour l'instant"}
            hint={dossiers.length ? "Modifiez la recherche ou le filtre." : "Créez votre premier dossier chantier : photos, notes et mesures sur place."}
            action={!dossiers.length && (
              <button type="button" data-testid="dossiers-empty-new" onClick={() => setShowForm(true)} className={`${btnPrimary} mt-2`}>
                <Plus className="h-4 w-4" /> Nouveau dossier
              </button>
            )}
          />
        )}
      </div>
    </StudioLayout>
  );
}
